import { routes } from "./router/routes";
import { Navigate, Route, Routes } from "react-router-dom";
import { Navbar } from "./components";
import { useDocumentTitle } from "./utils/useDocumentTitle";
import SessionTimeoutWarning from "./components/shared/SessionTimeoutWarning";
import { GET_AGENCY } from "./apis/queries/getAgency";
import { resolveIdleTimeoutMs, resolveWarningMs } from "./utils/sessionTimeout";
import { strings } from "./resources/strings";
import AuthLayout from "./_auth/pages/AuthLayout";
import RootLayout from "./_root/pages/RootLayout";
import ProtectedRoute, { DashboardRoute } from "./components/ProtectedRoute";
import "@aws-amplify/ui-react/styles.css";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DeviceInfoService from "./utils/DeviceInfo";
import { useEffect, useState } from "react";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { useIdleTimer } from "react-idle-timer";
import { GET_PARTICIPANT_CASE } from "./apis/queries/getParticipantCase";
import { useQuery } from "@apollo/client";
import Utils from "./utils/UtilityFunctions";

function App() {
  const { signOut, authStatus } = useAuthenticator((context) => [
    context.user,
    context.authStatus,
  ]);
  // SC 2.2.1 Timing Adjustable. This was a hardcoded ten minutes that signed
  // the participant out with no warning and no way to continue — and the
  // agency's own `sessionTimeoutMins`, already fetched by this query, was
  // consumed by nothing. A null/absent/zero value falls back to ten minutes
  // rather than producing an instant or infinite timeout.
  const { data: agencyData } = useQuery(GET_AGENCY);
  const idleTimeoutMs = resolveIdleTimeoutMs(
    agencyData?.getAgency?.sessionTimeoutMins,
  );
  const promptBeforeIdle = resolveWarningMs(idleTimeoutMs);

  const [timeoutWarningOpen, setTimeoutWarningOpen] = useState(false);
  // Seeded to 0 and set from the live timer when the warning opens, rather than
  // initialised from promptBeforeIdle: useState's initial value is captured on
  // the FIRST render, which happens before the agency query resolves. With a
  // short agency-configured timeout the countdown would have opened showing the
  // default's 60 and then jumped.
  const [secondsLeft, setSecondsLeft] = useState(0);
  const {
    data: caseData,
    //    loading: loadingCase,
    //    error: loadCaseError
  } = useQuery(GET_PARTICIPANT_CASE);

  const [isOnboarded, setIsOnboarded] = useState(false);

  if (
    !isOnboarded &&
    !Utils.isNullOrUndefined(
      caseData?.getParticipantCase?.onboardingCompletedAt,
    )
  ) {
    setIsOnboarded(true);
  }
  // SC 2.4.2 — every route shared one static <title> before this.
  useDocumentTitle();

  const handleIdle = () => {
    signOut();
    /**
     * This is a workaround to prevent an issue we are experiencing with the AWS Amplify
     * Authenticator component.
     *
     * The issue is reproduced via:
     * - 1. sign in, do not interact with the page (including moving the mouse) and wait for the idle time to be reach to force signOut
     * - the form input fields are not properly displayed. (Shows "Username" input instead of "Phone Number")
     *
     * After reloading, the form input fields are displayed correctly.
     */
    // eslint-disable-next-line no-restricted-globals
    location.reload();
  };
  const { activate, getRemainingTime } = useIdleTimer({
    timeout: idleTimeoutMs,
    promptBeforeIdle,
    onPrompt: () => {
      setSecondsLeft(Math.ceil(promptBeforeIdle / 1000));
      setTimeoutWarningOpen(true);
    },
    onIdle: handleIdle,
    // Any real interaction while the warning is up should dismiss it, not just
    // the button — a participant who moves the mouse is plainly still there.
    onActive: () => setTimeoutWarningOpen(false),
  });

  // Live countdown while the warning is showing.
  useEffect(() => {
    if (!timeoutWarningOpen) return;
    const tick = setInterval(
      () => setSecondsLeft(Math.max(0, Math.ceil(getRemainingTime() / 1000))),
      1000,
    );
    return () => clearInterval(tick);
  }, [timeoutWarningOpen, getRemainingTime]);

  // When the app loads, initialize the device info service
  // After init, the device info will be available via getDeviceInfo()
  DeviceInfoService.getInstance();

  useEffect(() => {
    const initialize = () => {
      // this is necessary to tell the difference between a reload and a new tab
      // if it's a refresh we don't want the user to sign out.
      // if it's a new tab the user should be signed out.
      // the user is signed out on a new tab because the event beforeunload when a tab is closed is the same event that happens on a refresh,
      // so we can't tell if we should sign out or or allow the refresh.
      if (
        sessionStorage.getItem("loaded") !== "yes" &&
        authStatus === "authenticated"
      ) {
        signOut();
        /**
         * This is a workaround to prevent an issue we are experiencing with the AWS Amplify
         * Authenticator component.
         *
         * The issue is reproduced via:
         * - 1. sign in, open a new tab, close the signed in tab, navigate back to login page on the new tab
         * - 2. sign in, close browser, open browser and enter login url
         * - the form input fields are not properly displayed. (Shows "Username" input instead of "Phone Number")
         *
         * After reloading, the form input fields are displayed correctly.
         */
        // eslint-disable-next-line no-restricted-globals
        location.reload();
        sessionStorage.setItem("loaded", "yes");
      }
    };

    initialize();
  }, [signOut, authStatus]);

  return (
    // A div, not <main>: this wrapper contains the navbar, and each page
    // renders its own <main>. Two of them nested is a 1.3.1 failure, and the
    // navigation does not belong inside the main landmark. data-theme stays
    // here — Chat and the Navbar carry none of their own.
    <div data-theme="repath" className="flex flex-col h-screen bg-accent">
      {/*
        SC 2.4.1 Bypass Blocks. The navbar repeats seven controls ahead of the
        content on every page. This must be the FIRST focusable element in the
        document and must become VISIBLE on focus — a permanently hidden skip
        link serves a screen reader and abandons the sighted keyboard user, who
        is mostly who 2.4.1 is for. Targets the page's single <main>, which
        Batch E established. tabIndex on the target is what makes focus (not
        just scroll) actually move there.
      */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-[100] focus:rounded focus:bg-white focus:px-4 focus:py-2 focus:text-primary focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-primary"
        onClick={(e) => {
          const main = document.querySelector("main");
          // Bail BEFORE preventDefault when there is no <main>: a route's
          // Suspense fallback (PageContentLoading) renders none, and swallowing
          // the click there left the link dead with the native #main-content
          // navigation suppressed too. Letting the default through is the
          // weaker behaviour but it is not nothing.
          if (!main) {
            return;
          }
          e.preventDefault();
          main.setAttribute("tabindex", "-1");
          (main as HTMLElement).focus();
          main.scrollIntoView();
        }}
      >
        {strings.skipToContent}
      </a>
      <SessionTimeoutWarning
        open={timeoutWarningOpen}
        secondsRemaining={secondsLeft}
        onContinue={() => {
          setTimeoutWarningOpen(false);
          activate();
        }}
      />
      {/* Nav will remove itself from the DOM when the app moves to the login page */}
      <Navbar />
      <ToastContainer position="top-center" theme="colored" />

      <Routes>
        {/* Public Routes */}
        <Route element={<AuthLayout />}>
          <Route path={routes.login.path} element={routes.login.element} />
        </Route>

        {/* Private Routes (requires auth) */}
        <Route
          path={routes.accessDenied.path}
          element={routes.accessDenied.element}
        />
        <Route element={<RootLayout />}>
          <Route
            path={routes.dashboard.path}
            element={
              <DashboardRoute
                setIsOnboarded={setIsOnboarded}
                isOnboarded={isOnboarded}
              />
            }
          />
          <Route
            path={routes.appointments.path}
            element={
              <ProtectedRoute
                setIsOnboarded={setIsOnboarded}
                isOnboarded={isOnboarded}
              >
                {routes.appointments.element}
              </ProtectedRoute>
            }
          />
          <Route
            path={routes.documents.path}
            element={
              <ProtectedRoute
                setIsOnboarded={setIsOnboarded}
                isOnboarded={isOnboarded}
              >
                {routes.documents.element}
              </ProtectedRoute>
            }
          />
          <Route
            path={routes.termsOfUse.path}
            element={
              <ProtectedRoute
                setIsOnboarded={setIsOnboarded}
                isOnboarded={isOnboarded}
              >
                {routes.termsOfUse.element}
              </ProtectedRoute>
            }
          />
          <Route
            path={routes.officerProfile.path}
            element={
              <ProtectedRoute
                setIsOnboarded={setIsOnboarded}
                isOnboarded={isOnboarded}
              >
                {routes.officerProfile.element}
              </ProtectedRoute>
            }
          />
          <Route
            path={routes.resources.path}
            element={
              <ProtectedRoute
                setIsOnboarded={setIsOnboarded}
                isOnboarded={isOnboarded}
              >
                {routes.resources.element}
              </ProtectedRoute>
            }
          />
          <Route
            path={routes.resourceDetail.path}
            element={
              <ProtectedRoute
                setIsOnboarded={setIsOnboarded}
                isOnboarded={isOnboarded}
              >
                {routes.resourceDetail.element}
              </ProtectedRoute>
            }
          />
        </Route>

        {/* Chat renders outside RootLayout: the page owns its full-height
            layout (composer pinned to the bottom) and has no Footer. */}
        <Route
          path={routes.chat.path}
          element={
            <ProtectedRoute
              setIsOnboarded={setIsOnboarded}
              isOnboarded={isOnboarded}
            >
              {routes.chat.element}
            </ProtectedRoute>
          }
        />

        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </div>
  );
}

export default App;
